// Thin client for the admin-only User Management endpoints
// (`/api/admin/users/...`), following the same apiRequest pattern as
// backendSync.ts. Every call is a no-op returning null/[] if there's no
// backend or the caller isn't an admin — the page decides what to show.
import { apiRequest } from "./api";

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined: string;
  committee_role: string | null;
  committee_district: string | null;
  owner_name: string | null;
}

const BASE = "/admin/users/";

export async function fetchUsers(): Promise<AdminUser[]> {
  const res = await apiRequest<{ results?: AdminUser[] } | AdminUser[]>(`${BASE}?page_size=200`);
  if (!res) return [];
  return Array.isArray(res) ? res : res.results ?? [];
}

export async function createUser(payload: {
  username: string;
  password: string;
  email?: string;
}): Promise<{ ok: boolean; error?: string; user?: AdminUser }> {
  let errorMsg = "Could not create the account.";
  const res = await apiRequest<AdminUser>(
    BASE,
    { method: "POST", body: JSON.stringify(payload) },
    (status, body) => {
      errorMsg = typeof body === "object" && body ? JSON.stringify(body) : `HTTP ${status}`;
    }
  );
  return res ? { ok: true, user: res } : { ok: false, error: errorMsg };
}

export async function updateUser(id: number, payload: Partial<Pick<AdminUser, "is_active" | "email">>): Promise<AdminUser | null> {
  return apiRequest<AdminUser>(`${BASE}${id}/`, { method: "PATCH", body: JSON.stringify(payload) });
}

export async function deleteUser(id: number): Promise<boolean> {
  let failed = false;
  await apiRequest<null>(`${BASE}${id}/`, { method: "DELETE" }, () => {
    failed = true;
  });
  return !failed;
}

export async function setCommittee(id: number, role: string, district: string): Promise<AdminUser | null> {
  return apiRequest<AdminUser>(`${BASE}${id}/set_committee/`, {
    method: "POST",
    body: JSON.stringify({ role, district }),
  });
}

export async function removeCommittee(id: number): Promise<AdminUser | null> {
  return apiRequest<AdminUser>(`${BASE}${id}/remove_committee/`, { method: "POST" });
}

// Mirrors CommitteeMember.ROLE_CHOICES in registry/models.py.
export const COMMITTEE_ROLES = [
  { value: "dc_convener", label: "Deputy Commissioner — Convener" },
  { value: "adc_secretary", label: "ADC (General) — Member/Secretary" },
  { value: "police_member", label: "Police Department representative — Member" },
  { value: "ctd_member", label: "CTD representative — Member" },
  { value: "special_branch_member", label: "Special Branch representative — Member" },
  { value: "dept_member", label: "Requesting Department head — Member" },
  { value: "caa_reviewer", label: "CAA registration reviewer" },
  { value: "co_opted", label: "Co-opted member" },
];
